import { Controller } from '@hotwired/stimulus'

// One profile/editor DOM supports both presentations: a horizontal push on
// narrow screens and a persistent master/detail split from the existing md
// breakpoint upwards. It also tells the server where a successful save should
// land without coupling persistence to viewport-specific forms.
export default class extends Controller {
  static targets = ['layout', 'detail', 'editorTemplate']
  static values = { editing: Boolean }

  connect() {
    this.desktop = window.matchMedia('(min-width: 768px)')
    this.updateLayout = this.updateLayout.bind(this)
    this.handlePopState = this.handlePopState.bind(this)
    this.desktop.addEventListener?.('change', this.updateLayout)
    window.addEventListener('popstate', this.handlePopState, { capture: true })
    this.updateLayout()

    if (this.editingValue && this.desktop.matches) {
      requestAnimationFrame(() => this.focusEditor())
    }
  }

  disconnect() {
    this.desktop?.removeEventListener?.('change', this.updateLayout)
    window.removeEventListener('popstate', this.handlePopState, { capture: true })
    window.cancelAnimationFrame(this.entranceFrame)
  }

  updateLayout() {
    const destination = this.desktop.matches ? 'detail' : 'master'
    this.layoutTargets.forEach((input) => { input.value = destination })
  }

  // Mobile Safari only opens its software keyboard when focus happens inside
  // the original user gesture. Mount the shared editor from an inert template
  // during the tap, focus it against its stable layout box, then start the same
  // translated entrance used by the selection editors on the following frame.
  openTextEditor(event) {
    if (this.desktop.matches || this.editingValue || !this.primaryClick(event)) return

    const field = event.params.field
    const template = this.editorTemplateTargets.find((candidate) => (
      candidate.dataset.profileSettingsField === field
    ))
    if (!template) return

    event.preventDefault()
    this.initialDetail = this.detailTarget.innerHTML
    this.instantEditing = true
    this.detailTarget.replaceChildren(template.content.cloneNode(true))
    this.element.classList.add('profile--editing', 'profile--text-preparing')
    this.editingValue = true
    this.markSelected(field)
    this.updateLayout()

    window.history.pushState(
      { ...window.history.state, rafaProfileEditor: field },
      '',
      event.currentTarget.href
    )

    // Commit the clipped, transform-free preparation state before focusing.
    void this.detailTarget.offsetWidth
    this.focusEditor()

    this.entranceFrame = window.requestAnimationFrame(() => {
      this.element.classList.remove('profile--text-preparing')
      this.entranceFrame = null
    })
  }

  closeTextEditor(event) {
    if (!this.instantEditing) return

    event.preventDefault()
    window.history.back()
  }

  handlePopState(event) {
    if (!this.instantEditing) return

    event.stopImmediatePropagation()
    this.detailTarget.innerHTML = this.initialDetail
    window.cancelAnimationFrame(this.entranceFrame)
    this.entranceFrame = null
    this.element.classList.remove('profile--editing', 'profile--text-preparing')
    this.editingValue = false
    this.instantEditing = false
    this.markSelected(null)
    this.updateLayout()
  }

  primaryClick(event) {
    return event.button === 0 &&
      !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
  }

  markSelected(field) {
    this.element.querySelectorAll('.profile-row.is-selected').forEach((row) => {
      row.classList.remove('is-selected')
      row.removeAttribute('aria-current')
    })

    if (!field) return
    const row = this.element.querySelector(`#profile_setting_${field}`)
    row?.classList.add('is-selected')
    row?.setAttribute('aria-current', 'page')
  }

  focusEditor() {
    const input = this.element.querySelector('[data-profile-settings-autofocus]')
    if (!input) return

    input.focus({ preventScroll: true })
    const length = input.value?.length
    if (typeof input.setSelectionRange === 'function' && Number.isInteger(length)) {
      input.setSelectionRange(length, length)
    }
  }

}
